import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useBoardUI } from "../store";

const previewUrlCache = new Map<string, { url: string; exp: number }>();
async function previewImage(hash: string): Promise<string> {
  const hit = previewUrlCache.get(hash);
  if (hit && hit.exp > Date.now() + 60_000) return hit.url;
  const r = await api<{ url: string; expiresAt: number }>(`/api/previews/${hash}/image`);
  previewUrlCache.set(hash, { url: r.url, exp: r.expiresAt });
  return r.url;
}

export interface LinkFields {
  url?: string;
  title?: string;
  description?: string;
  siteName?: string;
  imageHash?: string | null;
  faviconUrl?: string | null;
  embedProvider?: string | null;
  embedUrl?: string | null;
  showImage?: boolean;
  showDescription?: boolean;
  pending?: boolean;
}

/** Link preview card. Embeds (YouTube, Figma…) show a facade and load the iframe only on click. */
export function LinkBody({
  id,
  f,
  w,
  onSubmitUrl,
}: {
  id: string;
  f: LinkFields;
  w: number;
  onSubmitUrl?: (url: string) => void;
}) {
  const [img, setImg] = useState<string | null>(null);
  const t = useT();
  const { canEdit } = useBoard();
  const [playing, setPlaying] = useState(false);
  const selected = useBoardUI((s) => s.selection.has(id));
  useEffect(() => {
    if (!f.imageHash || f.showImage === false) return setImg(null);
    let alive = true;
    previewImage(f.imageHash)
      .then((u) => alive && setImg(u))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [f.imageHash, f.showImage]);
  const host = (() => {
    try {
      return new URL(f.url ?? "").hostname.replace(/^www\./, "");
    } catch {
      return f.url ?? "";
    }
  })();
  const isEmbed = !!f.embedUrl;
  const embedH = Math.round((w * 9) / 16);

  if (!f.url)
    return (
      <div className="px-3 py-3">
        <input
          autoFocus
          type="url"
          readOnly={!canEdit}
          placeholder={t.linkPlaceholder}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const v = (e.target as HTMLInputElement).value.trim();
              if (v) onSubmitUrl?.(v);
            }
          }}
          onPaste={(e) => {
            const v = e.clipboardData.getData("text/plain").trim();
            if (v) {
              e.preventDefault();
              onSubmitUrl?.(v);
            }
          }}
          className="w-full rounded-sm border border-border bg-panel-2 px-2 py-1.5 text-[13px] outline-none placeholder:text-dim"
        />
      </div>
    );

  if (isEmbed && playing)
    return (
      <div
        className="relative"
        style={{ height: embedH }}
        onPointerDown={(e) => selected && e.stopPropagation()}
      >
        <iframe
          title={f.title ?? host}
          src={f.embedUrl!}
          className="size-full border-0"
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          referrerPolicy="no-referrer"
        />
      </div>
    );

  return (
    <div className="flex flex-col">
      {img && f.showImage !== false ? (
        <div className="relative overflow-hidden" style={{ height: isEmbed ? embedH : Math.round(w * 0.52) }}>
          <img src={img} alt="" draggable={false} className="block size-full object-cover" />
          {isEmbed ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30"
              aria-label="Play"
            >
              <span className="flex size-14 items-center justify-center rounded-full bg-white/90 text-2xl text-black">
                ▶
              </span>
            </button>
          ) : null}
        </div>
      ) : isEmbed ? (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex items-center justify-center bg-panel-3 text-3xl"
          style={{ height: embedH }}
          aria-label="Open"
        >
          ▶
        </button>
      ) : null}
      <div className="px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted">
          {f.faviconUrl ? (
            <img src={f.faviconUrl} alt="" className="size-3.5 rounded-sm" draggable={false} />
          ) : null}
          <span className="truncate">{f.siteName || host}</span>
          {f.pending ? <span className="ml-auto animate-pulse">…</span> : null}
        </div>
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-1 line-clamp-2 block font-semibold leading-snug hover:underline"
          onPointerDown={(e) => selected && e.stopPropagation()}
        >
          {f.title || f.url}
        </a>
        {f.description && f.showDescription !== false ? (
          <p className="mt-1 line-clamp-3 text-[12px] leading-snug text-muted">{f.description}</p>
        ) : null}
      </div>
    </div>
  );
}
